import { WorkoutSection, WorkoutSuperset } from '@strength/domain-workout';
import { MdInsertLink } from '@teamworksdev/icons';
import { atom, useAtom } from 'jotai';
import { cn } from '../utils';

const selectedWorkoutSetIdAtom = atom<string | null>(null);

/**
 * Computes a map of superset IDs to their index in the workout.
 * This is computed once per workout to avoid recalculating on every render.
 */
export function getSupersetIndexMap(
  sections: WorkoutSection[],
): Map<string, number> {
  // Get all unique supersets from all sections, sorted by section sortOrder first, then superset sortOrder
  const allSupersets: Array<{
    superset: WorkoutSuperset;
    sectionSortOrder: number;
  }> = [];

  for (const section of sections) {
    const sectionSupersets = section.entries
      .filter(
        (entry): entry is WorkoutSuperset => entry.__type === 'WorkoutSuperset',
      )
      .map((superset) => ({
        superset,
        sectionSortOrder: section.sortOrder,
      }));

    allSupersets.push(...sectionSupersets);
  }

  // Sort by section sortOrder first, then superset sortOrder
  allSupersets.sort((a, b) => {
    if (a.sectionSortOrder !== b.sectionSortOrder) {
      return a.sectionSortOrder - b.sectionSortOrder;
    }
    return a.superset.sortOrder - b.superset.sortOrder;
  });

  // Create a map: supersetId -> index
  const indexMap = new Map<string, number>();
  allSupersets.forEach((item, index) => {
    indexMap.set(item.superset.workoutSupersetId, index);
  });

  return indexMap;
}

export const SupersetIcon = ({
  workoutSupersetId,
  supersetIndex,
}: {
  workoutSupersetId: string;
  supersetIndex: number;
}) => {
  const [selectedWorkoutSetId, setSelectedWorkoutSetIdAtom] = useAtom(
    selectedWorkoutSetIdAtom,
  );
  // Use whether the index is even or odd to determine color (even = blue, odd = green)
  const isBlue = supersetIndex % 2 === 0;
  const color = isBlue ? 'text-blue-500' : 'text-green-500';
  const selected = selectedWorkoutSetId === workoutSupersetId;
  return (
    <div
      className={cn([
        'rounded-full border px-1 flex justify-center',
        color,
        selected ? 'bg-blue-100' : 'bg-white',
      ])}
      onMouseOver={() => setSelectedWorkoutSetIdAtom(workoutSupersetId)}
      onMouseLeave={() => setSelectedWorkoutSetIdAtom(null)}
    >
      <MdInsertLink size={18} />
    </div>
  );
};
